import { useState, useEffect } from "react";
import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useWallet } from "@/contexts/WalletContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ReferralSystem } from "@/components/social/ReferralSystem";
import { ShareButtons } from "@/components/social/ShareButtons";
import { cn } from "@/lib/utils";
import {
  Home, Flame, Trophy, BarChart3, Radio, Store, Crosshair,
  UserCheck, Fish, Rocket, Plus, Settings, Loader2,
  TrendingUp, TrendingDown, Wallet, Activity, Clock, Star,
  Bell, Globe, Sparkles, MessageSquare, Code, DollarSign
} from "lucide-react";

interface QuickLink {
  id: string;
  icon: typeof Home;
  label: string;
  href: string;
  color: string;
}

const ALL_QUICK_LINKS: QuickLink[] = [
  { id: "home", icon: Home, label: "Home", href: "/", color: "bg-primary/10 text-primary" },
  { id: "discover", icon: Globe, label: "Discover", href: "/discover", color: "bg-blue-500/10 text-blue-500" },
  { id: "trending", icon: Flame, label: "Trending", href: "/trending", color: "bg-orange-500/10 text-orange-500" },
  { id: "new", icon: Sparkles, label: "New Coins", href: "/new", color: "bg-purple-500/10 text-purple-500" },
  { id: "leaderboard", icon: Trophy, label: "Leaderboard", href: "/leaderboard", color: "bg-yellow-500/10 text-yellow-500" },
  { id: "portfolio", icon: BarChart3, label: "Portfolio", href: "/portfolio", color: "bg-green-500/10 text-green-500" },
  { id: "streams", icon: Radio, label: "Streams", href: "/streams", color: "bg-red-500/10 text-red-500" },
  { id: "community", icon: MessageSquare, label: "Community Hub", href: "/community-hub", color: "bg-cyan-500/10 text-cyan-500" },
  { id: "marketplace", icon: Store, label: "Marketplace", href: "/marketplace", color: "bg-pink-500/10 text-pink-500" },
  { id: "sniper", icon: Crosshair, label: "Token Sniper", href: "/sniper", color: "bg-red-500/10 text-red-500" },
  { id: "copy-subs", icon: DollarSign, label: "Copy Subscriptions", href: "/copy-subscriptions", color: "bg-emerald-500/10 text-emerald-500" },
  { id: "copy-trading", icon: UserCheck, label: "Copy Trading", href: "/copy-trading", color: "bg-indigo-500/10 text-indigo-500" },
  { id: "whales", icon: Fish, label: "Whale Tracker", href: "/whales", color: "bg-blue-500/10 text-blue-500" },
  { id: "create", icon: Rocket, label: "Create Coin", href: "/create", color: "bg-primary/10 text-primary" },
  { id: "streamer", icon: Radio, label: "Streamer Dashboard", href: "/streamer", color: "bg-purple-500/10 text-purple-500" },
  { id: "api", icon: Code, label: "API Access", href: "/api", color: "bg-gray-500/10 text-gray-400" },
  { id: "settings", icon: Settings, label: "Settings", href: "/settings", color: "bg-gray-500/10 text-gray-400" },
];

const DEFAULT_LINKS = ["trending", "portfolio", "create", "sniper", "streams", "marketplace"];

export default function Dashboard() {
  const { connected, balance, address, publicKey, connect } = useWallet();
  const [selectedLinks, setSelectedLinks] = useState<string[]>(DEFAULT_LINKS);
  const [isEditing, setIsEditing] = useState(false);

  // Fetch real trades
  const { data: trades, isLoading: tradesLoading } = useQuery({
    queryKey: ["user-trades", publicKey],
    queryFn: async () => {
      if (!publicKey) return [];
      const { data } = await supabase
        .from("trades")
        .select("*, coins(name, ticker)")
        .eq("wallet", publicKey)
        .order("timestamp", { ascending: false })
        .limit(5);
      return data ?? [];
    },
    enabled: connected && !!publicKey,
  });

  // Fetch real watchlist
  const { data: watchlist, isLoading: watchlistLoading } = useQuery({
    queryKey: ["user-watchlist", publicKey],
    queryFn: async () => {
      if (!publicKey) return [];
      const { data } = await supabase
        .from("watchlists")
        .select("*, coins(*)")
        .eq("user_wallet", publicKey)
        .limit(5);
      return data ?? [];
    },
    enabled: connected && !!publicKey,
  });

  // Fetch notifications count
  const { data: notificationsCount } = useQuery({
    queryKey: ["notifications-count", publicKey],
    queryFn: async () => {
      if (!publicKey) return 0;
      const { count } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_wallet", publicKey)
        .eq("is_read", false);
      return count ?? 0;
    },
    enabled: connected && !!publicKey,
  });

  // Calculate portfolio value from trades
  const portfolioStats = {
    totalTrades: trades?.length || 0,
    portfolioValue: trades?.reduce((sum, t) => sum + (t.amount_sol || 0), 0) || 0,
  };

  // Load saved dashboard links
  useEffect(() => {
    const saved = localStorage.getItem("moonrun_dashboard_links");
    if (saved) {
      setSelectedLinks(JSON.parse(saved));
    }
  }, []);

  // Save dashboard links
  useEffect(() => {
    localStorage.setItem("moonrun_dashboard_links", JSON.stringify(selectedLinks));
  }, [selectedLinks]);

  const toggleLink = (id: string) => {
    setSelectedLinks((prev) =>
      prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id]
    );
  };

  const activeLinks = ALL_QUICK_LINKS.filter((link) => selectedLinks.includes(link.id));

  if (!connected) {
    return (
      <PageLayout>
        <Helmet>
          <title>Dashboard - TheMoonRun</title>
        </Helmet>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <Wallet className="w-16 h-16 text-muted-foreground/30 mb-4" />
          <h1 className="text-2xl font-display font-bold mb-2">Connect Your Wallet</h1>
          <p className="text-muted-foreground mb-6 max-w-md">
            Connect your Solana wallet to access your personalized dashboard with quick links, portfolio stats, and more.
          </p>
          <Button variant="hero" size="lg" onClick={connect}>
            <Wallet className="w-4 h-4" />
            Connect Wallet
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Helmet>
        <title>Dashboard - TheMoonRun</title>
        <meta name="description" content="Your personalized TheMoonRun dashboard" />
      </Helmet>

      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Welcome back, <span className="gradient-text">{address}</span>
          </h1>
          <p className="text-muted-foreground">Here's your trading overview</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/settings">
              <Settings className="w-4 h-4" />
              Settings
            </Link>
          </Button>
          <Button variant="outline" size="sm" className="relative" asChild>
            <Link to="/notifications">
              <Bell className="w-4 h-4" />
              {(notificationsCount || 0) > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">
                  {notificationsCount}
                </span>
              )}
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="p-4 rounded-xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Balance</span>
          </div>
          <p className="text-xl font-mono font-bold text-foreground">{balance.toFixed(4)} SOL</p>
        </div>

        <div className="p-4 rounded-xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Total Volume</span>
          </div>
          <p className="text-xl font-mono font-bold text-foreground">{portfolioStats.portfolioValue.toFixed(2)} SOL</p>
        </div>

        <div className="p-4 rounded-xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Total Trades</span>
          </div>
          <p className="text-xl font-mono font-bold text-foreground">{portfolioStats.totalTrades}</p>
        </div>

        <div className="p-4 rounded-xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Watchlist</span>
          </div>
          <p className="text-xl font-mono font-bold text-foreground">{watchlist?.length || 0}</p>
        </div>
      </div>

      {/* Quick Links */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Quick Links</h2>
          <Button
            variant={isEditing ? "default" : "ghost"}
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? "Done" : "Customize"}
          </Button>
        </div>

        {isEditing ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {ALL_QUICK_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => toggleLink(link.id)}
                className={cn(
                  "p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2",
                  selectedLinks.includes(link.id)
                    ? "border-primary bg-primary/10"
                    : "border-border bg-card hover:border-primary/50"
                )}
              >
                <link.icon className="w-5 h-5" />
                <span className="text-xs font-medium">{link.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {activeLinks.map((link) => (
              <Link
                key={link.id}
                to={link.href}
                className={cn(
                  "p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-all flex flex-col items-center gap-2 hover-lift",
                  link.color
                )}
              >
                <link.icon className="w-6 h-6" />
                <span className="text-sm font-medium text-foreground">{link.label}</span>
              </Link>
            ))}
            <button
              onClick={() => setIsEditing(true)}
              className="p-4 rounded-xl border-2 border-dashed border-border bg-card/50 hover:border-primary/50 transition-all flex flex-col items-center gap-2"
            >
              <Plus className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Add More</span>
            </button>
          </div>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Recent Activity */}
        <div className="p-5 rounded-xl bg-card border border-border">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Recent Activity
          </h3>
          {tradesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : trades && trades.length > 0 ? (
            <div className="space-y-3">
              {trades.map((trade: any) => (
                <div key={trade.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center",
                      trade.type === "buy" ? "bg-success/10" : "bg-destructive/10"
                    )}>
                      {trade.type === "buy" ? (
                        <TrendingUp className="w-4 h-4 text-success" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-destructive" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-foreground text-sm">
                        {trade.type === "buy" ? "Bought" : "Sold"} ${trade.coins?.ticker || "Token"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(trade.timestamp * 1000).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-foreground">{trade.amount_sol?.toFixed(4)} SOL</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No trades yet</p>
          )}
          <Button variant="ghost" className="w-full mt-3" asChild>
            <Link to="/portfolio">View All Activity</Link>
          </Button>
        </div>

        {/* Watchlist */}
        <div className="p-5 rounded-xl bg-card border border-border">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Watchlist
          </h3>
          {watchlistLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : watchlist && watchlist.length > 0 ? (
            <div className="space-y-3">
              {watchlist.map((item: any) => (
                <Link
                  key={item.id}
                  to={`/coin/${item.coins?.id}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      {item.coins?.image ? (
                        <img src={item.coins.image} alt="" className="w-6 h-6 rounded" />
                      ) : (
                        <span className="text-xs font-bold text-primary">{item.coins?.ticker?.[0]}</span>
                      )}
                    </div>
                    <span className="font-medium text-foreground">${item.coins?.ticker}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-foreground">${item.coins?.price?.toFixed(6)}</p>
                    <p className={cn(
                      "text-xs font-medium",
                      (item.coins?.price_change_24h || 0) >= 0 ? "text-success" : "text-destructive"
                    )}>
                      {(item.coins?.price_change_24h || 0) >= 0 ? "+" : ""}{(item.coins?.price_change_24h || 0).toFixed(1)}%
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No coins in watchlist</p>
          )}
          <Button variant="ghost" className="w-full mt-3" asChild>
            <Link to="/trending">Discover Coins</Link>
          </Button>
        </div>
      </div>

      {/* Referral System */}
      <div className="mb-6">
        <ReferralSystem />
      </div>

      {/* Share CTA */}
      <div className="p-6 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 text-center">
        <h3 className="font-display text-lg font-bold text-foreground mb-2">
          Share TheMoonRun & Earn! 🚀
        </h3>
        <p className="text-muted-foreground text-sm mb-4">
          Share your referral link and earn rewards when friends trade
        </p>
        <div className="flex justify-center">
          <ShareButtons 
            title="Join me on TheMoonRun - the best Solana meme coin launchpad! 🚀🌙"
            url={window.location.origin}
          />
        </div>
      </div>
    </PageLayout>
  );
}
